import { Button, message, Typography, Spin } from "antd";
import FilterComponent from "components/Configuration/CommonComponents/FilterComponent";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { cglFormatDate } from "utils/functions";
import { getData } from "../../../../apiServices/common";
import AppContent from "../../../../common/AppContent";
import HandleNotification from "../../../../common/Notification";
import AppPageHeader from "../../../../common/PageHeader";
import ExtraContent from "../../../Configuration/CommonComponents/ExtraContent";
import SewingToFoldingTable from "./MismatchReportTable";
import MismatchReportTable from "./MismatchReportTable";
import { constructTitle } from "../helpers";
import moment from "moment";
import { GENERATE_MISMATCH_REPORT } from "apiServices/API_ENDPOINTS";
import { DownloadOutlined, LoadingOutlined } from "@ant-design/icons";

// Default filter on open: today's entry_date, Line/Style/Sub-style unset.
const getDefaultFilterValues = () => ({ entry_date: moment() });

const MismatchReport = () => {
  // Router

  const location = useLocation();
  const routerPath = location ? location.pathname : null;
  const navigate = useNavigate();

  // States
  const [showFilter, setShowFilter] = useState(
    localStorage.getItem("filter_status") && routerPath
      ? JSON.parse(localStorage.getItem("filter_status"))[routerPath]
      : false,
  );
  const [page, setPage] = useState(1);
  const [refresh, setRefresh] = useState(0);
  const [tableData, setTableData] = useState(null);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [filterValues, setFilterValues] = useState(getDefaultFilterValues());
  console.log("🚀 ~ HourlyIron ~ filterValues:", filterValues);

  // Antd
  const { Title } = Typography;

  // Add a new state for tracking download status
  const [downloading, setDownloading] = useState(false);
  // Add a new state for tracking table loading
  const [tableLoading, setTableLoading] = useState(false);

  // Filter open/close status update in localStorage
  useEffect(() => {
    const filterStatus = localStorage.getItem("filter_status");
    const parsedFilterStatus = filterStatus ? JSON.parse(filterStatus) : {};

    if (showFilter) {
      parsedFilterStatus[routerPath] = true;
      localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
    } else {
      parsedFilterStatus[routerPath] = false;
      localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
    }
  }, [showFilter, routerPath, location]);

  const onFilterChange = (values) => {
    setPage(1);
    setFilterValues(values || {});
  };

  const fetchData = useCallback(
    async (filterValues) => {
      // Check if entry_date is present before proceeding
      if (!filterValues?.entry_date) {
        setTableData([]);
        setTotal(0);
        return;
      }

      setTableLoading(true); // Start loading
      const query = `/api/digitalization/get-mismatch-report`;
      const bodyData = {
        page: page,
        per_page: filterValues?.per_page || 10,
        ...(filterValues?.line_id && {
          line_id: filterValues?.line_id,
        }),
        ...(filterValues?.style_id && { style_id: filterValues?.style_id }),
        ...(filterValues?.sub_style_id && {
          sub_style_id: filterValues?.sub_style_id,
        }),
        ...(filterValues?.entry_date && {
          entry_date: moment(filterValues?.entry_date).format("DD-MM-YYYY"),
        }),
      };

      try {
        const response = await getData(query, false, bodyData);

        if (response && response?.data?.code === 200) {
          setTableData(response?.data?.data);
          setPageSize(response?.data?.data?.per_page);
          setTotal(response?.data?.data?.total);
        } else {
          HandleNotification("error", "Error", "Something went wrong");
        }
      } catch (error) {
        HandleNotification("error", "Error", "Failed to fetch data");
      } finally {
        setTableLoading(false); // End loading regardless of result
      }
    },
    [page],
  );

  useEffect(() => {
    const value = {
      ...filterValues,
      page: page,
    };

    // Only fetch data if entry_date is present
    if (value.entry_date) {
      fetchData(value);
    } else {
      // Clear table data when no date is present
      setTableData([]);
      setTotal(0);
    }
  }, [refresh, page, pageSize, filterValues, fetchData]);

  const onPageChange = (newPage) => {
    setPage(newPage);
  };

  const tableColumns = [
    {
      title: "Hour",
      dataIndex: "hour",
      key: "hour",
      width: "60px",
      render: (text, record) =>
        `${record.hour} (${record.hour_time_range_from} - ${record.hour_time_range_to})`,
    },
    {
      title: "Line",
      dataIndex: ["line_info", "name"],
      key: "line",
      width: "100px",
    },
    {
      title: "Sales/Sub Contract",
      dataIndex: ["sales_contract_info", "reference_no"],
      key: "sales_contract",
      width: "150px",
    },
    {
      title: "Buying Office",
      dataIndex: ["buying_office_info", "name"],
      key: "buying_office",
      width: "150px",
    },
    {
      title: "Buyer",
      dataIndex: ["buyer_info", "name"],
      key: "buyer",
      width: "150px",
    },
    {
      title: "Factory",
      dataIndex: ["sub_contract_factory_info", "name"],
      key: "factory",
      width: "120px",
      render: (text) => text || "N/A",
    },
    {
      title: "Style",
      dataIndex: ["style_info", "style_no"],
      key: "style",
      width: "120px",
    },
    {
      title: "Sub-Style",
      dataIndex: ["sub_style_info", "sub_style_no"],
      key: "sub_style",
      width: "120px",
      render: (text) => text || "N/A",
    },
    {
      title: "Color",
      dataIndex: ["color_info", "name"],
      key: "color",
      width: "120px",
    },
    {
      title: "Size",
      dataIndex: ["size_info", "name"],
      key: "size",
      width: "80px",
    },
    {
      title: "Quantity",
      dataIndex: "quantity",
      key: "quantity",
      width: "80px",
    },
    {
      title: "Mismatch",
      dataIndex: "mismatch_quantity",
      key: "mismatch_quantity",
      width: "80px",
    },
  ];

  const refetch = useCallback(() => {
    setRefresh((prevRefresh) => prevRefresh + 1);
  }, []);

  const exportDetails = useCallback(
    async (filterValues) => {
      // Prevent multiple downloads
      if (downloading) {
        return;
      }

      const { entry_date, style_id, line_id, sub_style_id } = filterValues || {};

      // Check if entry_date is present before proceeding
      if (!entry_date) {
        message.error({
          content: "Entry Date is required for download",
          duration: 3,
        });
        return;
      }

      // Set downloading state to true before starting download
      setDownloading(true);

      const bodyData = {
        entry_date: moment(entry_date).format("DD-MM-YYYY"),
        ...(style_id && { style_id }),
        ...(line_id && { line_id }),
        ...(sub_style_id && { sub_style_id }),
      };

      const query = `${GENERATE_MISMATCH_REPORT}`;

      try {
        message.loading({
          content: (
            <span>
              <span style={{ marginLeft: 8 }}>Downloading report...</span>
            </span>
          ),
          key: "downloadMessage",
          duration: 0,
        });

        const response = await getData(
          query,
          false,
          bodyData,
          { "Content-Type": "blob" },
          "arraybuffer",
        );

        if (response && response?.data) {
          // Download the file
          const outputFilename = `mismatch_report.xlsx`;

          const url = URL.createObjectURL(
            new Blob([response.data], {
              type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            }),
          );
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", outputFilename);
          document.body.appendChild(link);
          link.click();
          link.remove();

          message.success({
            content: "Mismatch Report Exported Successfully",
            key: "downloadMessage",
            duration: 2,
          });
          refetch();
        } else {
          message.error({
            content: "Something went wrong",
            key: "downloadMessage",
            duration: 3,
          });
        }
      } catch (error) {
        message.error({
          content: "Failed to download report",
          key: "downloadMessage",
        });
      } finally {
        // Always reset downloading state when finished
        setDownloading(false);
      }
    },
    [refetch, downloading],
  );

  const memoizedCustomFilter = useMemo(
    () => (
      <FilterComponent
        tableData={tableData}
        filterAPI={(value) => onFilterChange(value)}
        filterName={"mismatch-report-filter"}
        setShowFilter={setShowFilter}
        showFilter={showFilter}
        preview={downloading ? "Downloading..." : "Download"}
        isPreview={true}
        previewFunction={exportDetails}
        previewButtonProps={{
          loading: downloading,
          disabled: downloading,
          icon: <DownloadOutlined />,
          type: "primary",
        }}
      />
    ),
    [showFilter, tableData, exportDetails, downloading],
  );

  const header = (
    <AppPageHeader
      extra={
        <ExtraContent
          refresh={refresh}
          setRefresh={setRefresh}
          handleAddItemOpen={onFilterChange}
          hideAddButton={true}
          setShowFilter={setShowFilter}
          showFilter={showFilter}
        />
      }
      title={<Title level={5}>{"Mismatch Report"}</Title>}
    />
  );

  // Fix round 1 (ux-sweep batch 2, finding 4): spec A3 hint — the mount
  // fetch's default window (today's entry_date) is confirmed to return zero
  // rows on this data snapshot. MismatchReportTable's own <Table> doesn't
  // forward a `locale`/emptyText prop (and it is outside Task 2's edit
  // list), so the hint is rendered here instead, next to the table.
  const showEmptyHint = !tableLoading && Array.isArray(tableData) && tableData.length === 0;
  const emptyHintDate = filterValues?.entry_date
    ? moment(filterValues.entry_date).format("DD-MMM-YYYY")
    : moment().format("DD-MMM-YYYY");

  const content = (
    <>
      {memoizedCustomFilter}

      <Spin spinning={tableLoading} tip="Loading data...">
        <MismatchReportTable
          onChange={(newPage) => {
            onPageChange(newPage);
          }}
          total={total}
          current={page}
          defaultCurrent={page}
          pageSize={pageSize}
          dataSource={tableData}
          tableColumns={tableColumns}
          pagination={true}
          loading={false} // We're controlling loading with our own Spin component
        />
        {showEmptyHint && (
          <Typography.Text
            type="secondary"
            style={{ display: "block", marginTop: 8, marginBottom: 8, paddingLeft: 8 }}
          >
            {`No records for ${emptyHintDate}. Change the date above.`}
          </Typography.Text>
        )}
      </Spin>
    </>
  );
  return (
    <>
      <AppContent header={header} content={content} />
    </>
  );
};

export default MismatchReport;
